import { CheckIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

export interface CardSelectCheckboxProps {
  checked?: boolean;
  onToggle: () => void;
  ariaLabel?: string;
  /**
   * `lg` (32px frame, 22px box) floats over cards; `md` (24px frame, 16.5px
   * box) sits in row flows. Both follow the "Checkbox Item" component:
   * the md unchecked state in node 1632:44004 (16.5px box, 4px radius,
   * 1.5px `#E5E7EB` ring, white center) and the card reference 1597:13054
   * (22px box, 4px radius, `#FF6D00` fill, ~10.7×8 white check).
   */
  size?: 'md' | 'lg';
  className?: string;
}

const FRAME = {
  md: 'size-6',
  lg: 'size-8',
} as const;

/** The painted box, centered in the frame — never the frame itself. */
const BOX = {
  md: 'size-[16.5px] rounded-[4px]',
  lg: 'size-[22px] rounded-[4px]',
} as const;

const RING = {
  md: 'border-[1.5px]',
  lg: 'border-2',
} as const;

/** Check ink ≈ the design glyph: 8×5.5 in md, 10.7×7.3 in lg. */
const CHECK = {
  md: 'size-3',
  lg: 'size-4',
} as const;

/**
 * Floating selection checkbox.
 *
 * The frame is the hit area; the painted box is centered inside it, so the
 * visual never touches the frame's edges.
 *
 * Positioning ships as overridable defaults (`absolute right-1 bottom-1`) so
 * row layouts (branches, promotions) can re-place the same visual via
 * `className` without forking the pixels.
 */
export function CardSelectCheckbox({
  checked = false,
  onToggle,
  ariaLabel = 'Выбрать',
  size = 'lg',
  className,
}: CardSelectCheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      className={cn(
        'absolute right-1 bottom-1 z-10 flex items-center justify-center focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        FRAME[size],
        className,
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center transition-all duration-150',
          BOX[size],
          checked
            ? 'border-[#ff6d00] bg-[#ff6d00] text-white'
            : cn(RING[size], 'border-[#E5E7EB] bg-white text-transparent'),
        )}
      >
        <CheckIcon
          className={cn(
            CHECK[size],
            'stroke-[3]',
            checked ? 'text-white' : 'text-transparent',
          )}
        />
      </span>
    </button>
  );
}
